import { Injectable, inject } from '@angular/core'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { GbInfoboothEditComponent } from 'libs/gb-dashboard/setup/feature/src/lib/gb-infobooth-edit/gb-infobooth-edit.component'
import { GbLoaderService } from '@galabid/shared/utils/loader'
import { catchError, exhaustMap, finalize, map, of, switchMap, tap, withLatestFrom } from 'rxjs'
import { InfoboothsApiService } from './infobooths-api.service'
import * as InfoboothsActions from './infobooths.actions'
import { InfoboothsFacade } from './infobooths.facade'
import { InfoboothDialogFunctionEnum, INFOBOOTHS_LOADER, InfoboothsEntity } from './infobooths.models'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'

@Injectable()
export class InfoboothsEffects {
  private actions$ = inject(Actions)

  loadInfobooths$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InfoboothsActions.loadInfobooths),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) => {
        return this.infoboothsService.getInfobooths(urlName).pipe(
          map((response: InfoboothsEntity[]) => {
            return InfoboothsActions.loadInfoboothsSuccess({
              infobooths: response,
            })
          }),
          catchError((error) => {
            return of(InfoboothsActions.loadInfoboothsFailure({ error }))
          }),
        )
      }),
    ),
  )

  openEditInfoboothDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(InfoboothsActions.selectInfobooth),
        exhaustMap(() =>
          this.gbDashboardDialogService.getDialogObs(GbInfoboothEditComponent, {
            disableClose: true,
            maxWidth: 800,
            mobileFullScreen: true,
            data: {
              title: 'SETTINGS.INFOBOOTH.EDIT',
              submitLabel: 'SAVE',
              submitFunction: InfoboothDialogFunctionEnum.Update,
            },
          }),
        ),
      ),
    { dispatch: false },
  )

  openCreateInfoboothDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(InfoboothsActions.openCreateInfoboothDialog),
        exhaustMap(() =>
          this.gbDashboardDialogService.getDialogObs(GbInfoboothEditComponent, {
            disableClose: true,
            maxWidth: 800,
            data: {
              title: 'SETTINGS.INFOBOOTH.CREATE',
              submitLabel: 'CREATE',
              submitFunction: InfoboothDialogFunctionEnum.Create,
            },
          }),
        ),
      ),
    { dispatch: false },
  )

  deleteInfobooth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InfoboothsActions.deleteInfobooth),
      exhaustMap((action) =>
        this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'SETTINGS.INFOBOOTH.CONFIRM_DELETE_TITLE',
            body: 'SETTINGS.INFOBOOTH.CONFIRM_DELETE_BODY',
            confirmButtonText: 'SETTINGS.INFOBOOTH.CONFIRM_DELETE_CONFIRM',
            cancelButtonText: 'SETTINGS.INFOBOOTH.CONFIRM_DELETE_CANCEL',
          },
          action,
        ),
      ),
      tap((action) => {
        this.gbLoaderService.showLoaderDialog(
          INFOBOOTHS_LOADER.deleteInfobooth.id,
          INFOBOOTHS_LOADER.deleteInfobooth.message,
        )
      }),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) =>
        this.infoboothsService.deleteInfobooth(urlName, action.uuid).pipe(
          map(() =>
            InfoboothsActions.deleteInfoboothSuccess({
              uuid: action.uuid,
            }),
          ),
          catchError((error) => of(InfoboothsActions.deleteInfoboothFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(INFOBOOTHS_LOADER.deleteInfobooth.id)),
        ),
      ),
    ),
  )

  updateInfobooth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InfoboothsActions.updateSelectedInfobooth),
      withLatestFrom(this.infoboothsFacade.selectedInfobooths$),
      switchMap(([action, selectedInfobooth]) => {
        const payload: InfoboothsEntity = {
          ...selectedInfobooth,
          ...action.infobooth,
        } as InfoboothsEntity
        return this.infoboothsService.updateInfobooth(payload).pipe(
          map((response: InfoboothsEntity) => {
            return InfoboothsActions.updateSelectedInfoboothSuccess({
              update: {
                id: response.uuid,
                changes: response,
              },
            })
          }),
          catchError((error) => {
            return of(InfoboothsActions.updateSelectedInfoboothFailure({ error }))
          }),
        )
      }),
    ),
  )

  createInfobooth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InfoboothsActions.createInfobooth),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        const payload: InfoboothsEntity = {
          ...action.infobooth,
          auction: urlName,
        } as InfoboothsEntity
        return this.infoboothsService.createInfobooth(payload).pipe(
          map((response: InfoboothsEntity) => {
            return InfoboothsActions.createInfoboothSuccess({
              infobooth: response,
            })
          }),
          catchError((error) => {
            return of(InfoboothsActions.createInfoboothFailure({ error }))
          }),
        )
      }),
    ),
  )

  closeEditInfoboothDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(InfoboothsActions.updateSelectedInfoboothSuccess, InfoboothsActions.createInfoboothSuccess),
        map(() => this.gbDashboardDialogService.closeDialog(GbInfoboothEditComponent, true)),
      ),
    { dispatch: false },
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          InfoboothsActions.updateSelectedInfoboothFailure,
          InfoboothsActions.createInfoboothFailure,
          InfoboothsActions.deleteInfoboothFailure,
        ),
        map((action) => {
          const error = this.gbErrorMessageService.getErrorMessage(action.error.error)
          if (error && typeof error == 'string') {
            this.gbNotificationService.error(error)
          } else {
            this.gbNotificationService.error('SETTINGS.INFOBOOTH.INFOBOOTH_ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  showUpdateSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(InfoboothsActions.updateSelectedInfoboothSuccess),
        tap(() => {
          this.gbNotificationService.success('SETTINGS.INFOBOOTH.INFOBOOTH_UPDATED_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  showCreateSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(InfoboothsActions.createInfoboothSuccess),
        tap(() => {
          this.gbNotificationService.success('SETTINGS.INFOBOOTH.INFOBOOTH_CREATE_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  showDeleteSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(InfoboothsActions.deleteInfoboothSuccess),
        tap(() => {
          this.gbNotificationService.success('SETTINGS.INFOBOOTH.INFOBOOTH_DELETE_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private infoboothsService: InfoboothsApiService,
    private infoboothsFacade: InfoboothsFacade,
    private selectedAuctionFacade: SelectedAuctionFacade,
    private gbDashboardDialogService: GbDashboardDialogService,
    private gbLoaderService: GbLoaderService,
    private gbNotificationService: GbNotificationService,
    private gbErrorMessageService: GbErrorMessageService,
  ) {}
}
